import React, { useEffect, useState } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Upload as UploadIcon, FileText, ArrowLeft } from 'lucide-react'
import QuestionFormWithImage from '@/components/QuestionFormWithImage'
import CSVImportForm from '@/components/CSVImportForm'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export default function AdminQuestionCreation() {
  const { id } = useParams() // Get question ID from URL if editing
  const navigate = useNavigate()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [questionData, setQuestionData] = useState<any>(null)

  useEffect(() => {
    const loadQuestion = async () => {
      if (!id) {
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('questions')
          .select(
            `
            *,
            choices (
              id,
              label,
              text,
              is_correct
            )
          `
          )
          .eq('id', id)
          .single()

        if (error) throw error

        if (data) {
          setQuestionData({
            id: data.id,
            text: data.text,
            category: data.category,
            image_path: data.image_path,
            choices: data.choices,
          })
        }
      } catch (error) {
        console.error('Error loading question:', error)
        toast({
          description: 'Soru yüklenirken bir hata oluştu!',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    }

    loadQuestion()
  }, [id, toast])

  if (loading) {
    return (
      <AdminLayout>
        <div>Loading...</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center">
          <Link to="/admin/questions">
            <Button size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Geri Dön
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Manuel Soru Ekleme
            </TabsTrigger>
            <TabsTrigger value="excel" className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4" />
              Excel İle Toplu Ekleme
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-6">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {id ? 'Soru Düzenle' : 'Manuel Soru Ekleme'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <QuestionFormWithImage
                    initialData={questionData}
                    onSuccess={() => {
                      navigate('/admin/questions')
                      toast({
                        description: id ? 'Soru güncellendi!' : 'Soru eklendi!',
                      })
                    }}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="excel" className="mt-6">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UploadIcon className="h-5 w-5" />
                    Excel İle Toplu Soru Ekleme
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CSVImportForm />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  )
}
